import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import { Card, CardBody } from "../../../styles/components/ModernCard";
import Chart from "chart.js/auto";
import ReportGenerator from "../ReportGenerator/ReportGenerator";

// Styled components
const ChartsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.75rem;
  margin-bottom: 2.5rem;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled(Card)`
  position: relative;
  overflow: hidden;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(148, 163, 184, 0.2);
  transition: all 0.3s ease;
  min-height: 350px;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2), 0 0 15px rgba(99, 102, 241, 0.3);
  }

  /* Space background gradients */
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at ${(props) => props.gradientPos || "30% 70%"},
        rgba(99, 102, 241, 0.12) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at ${(props) => props.gradientPos2 || "70% 30%"},
        rgba(124, 58, 237, 0.08) 0%,
        transparent 50%
      );
    border-radius: inherit;
    z-index: -1;
    opacity: 0.8;
  }
`;

const ChartTitle = styled.h3`
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.25rem;
  margin-bottom: 1rem;
  font-weight: 500;
  text-align: center;
`;

const ChartContainer = styled.div`
  position: relative;
  height: 300px;
  width: 100%;
`;

const DetailedStatisticsCharts = ({
  reportStats,
  reviewTimeStats,
  courseStats,
  companyRatingStats,
  internshipCountStats,
}) => {
  const chartRefs = useRef({});
  const gradientPositions = [
    { pos1: "20% 80%", pos2: "80% 20%" },
    { pos1: "30% 60%", pos2: "70% 40%" },
    { pos1: "40% 30%", pos2: "60% 70%" },
    { pos1: "10% 50%", pos2: "90% 30%" },
  ];

  // Helper function to safely create a chart
  const createChart = (elementId, config) => {
    setTimeout(() => {
      const element = document.getElementById(elementId);
      if (!element) {
        console.warn(`Element with id ${elementId} not found`);
        return;
      }

      try {
        // Check if there's already a chart instance attached to this canvas
        if (Chart.getChart(element)) {
          Chart.getChart(element).destroy();
        }

        // Create new chart and store in ref
        chartRefs.current[elementId] = new Chart(element, config);
      } catch (error) {
        console.error(`Error creating chart for ${elementId}:`, error);
      }
    }, 0);
  };

  // Cleanup function to destroy charts
  const cleanupCharts = () => {
    Object.values(chartRefs.current).forEach((chart) => {
      if (chart && typeof chart.destroy === "function") {
        try {
          chart.destroy();
        } catch (error) {
          console.error("Error destroying chart:", error);
        }
      }
    });
    chartRefs.current = {};
  };

  useEffect(() => {
    // Clean up any existing charts first
    cleanupCharts();

    // Create Report Statistics Chart
    createChart("reportStatsChart", {
      type: "pie",
      data: {
        labels: ["Accepted", "Rejected", "Flagged"],
        datasets: [
          {
            data: [
              reportStats.accepted,
              reportStats.rejected,
              reportStats.flagged,
            ],
            backgroundColor: [
              "rgba(99, 202, 143, 0.8)",
              "rgba(239, 68, 68, 0.8)",
              "rgba(245, 158, 11, 0.8)",
            ],
            borderColor: [
              "rgba(99, 202, 143, 1)",
              "rgba(239, 68, 68, 1)",
              "rgba(245, 158, 11, 1)",
            ],
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              color: "rgba(255, 255, 255, 0.7)",
              font: {
                size: 12,
              },
            },
          },
          title: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                const label = context.label || "";
                const value = context.raw || 0;
                const total = context.chart.data.datasets[0].data.reduce(
                  (sum, val) => sum + val,
                  0
                );
                const percentage = Math.round((value / total) * 100);
                return `${label}: ${value} (${percentage}%)`;
              },
            },
          },
        },
      },
    });

    // Create Review Time Chart
    createChart("reviewTimeChart", {
      type: "bar",
      data: {
        labels: Object.keys(reviewTimeStats),
        datasets: [
          {
            label: "Average Review Time (Days)",
            data: Object.values(reviewTimeStats),
            backgroundColor: "rgba(99, 102, 241, 0.6)",
            borderColor: "rgba(99, 102, 241, 1)",
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: "rgba(255, 255, 255, 0.1)",
            },
            ticks: {
              color: "rgba(255, 255, 255, 0.7)",
            },
          },
          x: {
            grid: {
              color: "rgba(255, 255, 255, 0.1)",
            },
            ticks: {
              color: "rgba(255, 255, 255, 0.7)",
            },
          },
        },
        plugins: {
          legend: {
            labels: {
              color: "rgba(255, 255, 255, 0.7)",
            },
          },
        },
      },
    });

    // Create Top Courses Chart
    createChart("topCoursesChart", {
      type: "doughnut",
      data: {
        labels: Object.keys(courseStats),
        datasets: [
          {
            data: Object.values(courseStats),
            backgroundColor: [
              "rgba(99, 102, 241, 0.8)",
              "rgba(124, 58, 237, 0.8)",
              "rgba(217, 70, 239, 0.8)",
              "rgba(236, 72, 153, 0.8)",
              "rgba(244, 114, 182, 0.8)",
            ],
            borderColor: [
              "rgba(99, 102, 241, 1)",
              "rgba(124, 58, 237, 1)",
              "rgba(217, 70, 239, 1)",
              "rgba(236, 72, 153, 1)",
              "rgba(244, 114, 182, 1)",
            ],
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "right",
            labels: {
              color: "rgba(255, 255, 255, 0.7)",
              font: {
                size: 11,
              },
            },
          },
        },
      },
    });

    // Create Top Rated Companies Chart
    createChart("topRatedCompaniesChart", {
      type: "bar",
      data: {
        labels: Object.keys(companyRatingStats),
        datasets: [
          {
            label: "Average Rating",
            data: Object.values(companyRatingStats),
            backgroundColor: "rgba(56, 189, 248, 0.6)",
            borderColor: "rgba(56, 189, 248, 1)",
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: "y",
        scales: {
          x: {
            beginAtZero: true,
            max: 5,
            grid: {
              color: "rgba(255, 255, 255, 0.1)",
            },
            ticks: {
              color: "rgba(255, 255, 255, 0.7)",
            },
          },
          y: {
            grid: {
              color: "rgba(255, 255, 255, 0.1)",
            },
            ticks: {
              color: "rgba(255, 255, 255, 0.7)",
            },
          },
        },
        plugins: {
          legend: {
            labels: {
              color: "rgba(255, 255, 255, 0.7)",
            },
          },
        },
      },
    });

    // Create Top Companies by Internship Count Chart
    createChart("topCompaniesByCountChart", {
      type: "bar",
      data: {
        labels: Object.keys(internshipCountStats),
        datasets: [
          {
            label: "Internship Count",
            data: Object.values(internshipCountStats),
            backgroundColor: "rgba(34, 211, 238, 0.6)",
            borderColor: "rgba(34, 211, 238, 1)",
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: "y",
        scales: {
          x: {
            beginAtZero: true,
            grid: {
              color: "rgba(255, 255, 255, 0.1)",
            },
            ticks: {
              color: "rgba(255, 255, 255, 0.7)",
            },
          },
          y: {
            grid: {
              color: "rgba(255, 255, 255, 0.1)",
            },
            ticks: {
              color: "rgba(255, 255, 255, 0.7)",
            },
          },
        },
        plugins: {
          legend: {
            labels: {
              color: "rgba(255, 255, 255, 0.7)",
            },
          },
        },
      },
    });

    // Cleanup on component unmount
    return cleanupCharts;
  }, [
    reportStats,
    reviewTimeStats,
    courseStats,
    companyRatingStats,
    internshipCountStats,
  ]);

  return (
    <ChartsContainer>
      <ChartCard
        gradientPos={gradientPositions[0].pos1}
        gradientPos2={gradientPositions[0].pos2}
      >
        <CardBody>
          <ChartTitle>Report Statistics</ChartTitle>
          <ChartContainer>
            <canvas id="reportStatsChart"></canvas>
          </ChartContainer>
        </CardBody>
      </ChartCard>
      <ChartCard
        gradientPos={gradientPositions[1].pos1}
        gradientPos2={gradientPositions[1].pos2}
      >
        <CardBody>
          <ChartTitle>Average Review Time</ChartTitle>
          <ChartContainer>
            <canvas id="reviewTimeChart"></canvas>
          </ChartContainer>
        </CardBody>
      </ChartCard>
      <ChartCard
        gradientPos={gradientPositions[2].pos1}
        gradientPos2={gradientPositions[2].pos2}
      >
        <CardBody>
          <ChartTitle>Most Frequently Used Courses</ChartTitle>
          <ChartContainer>
            <canvas id="topCoursesChart"></canvas>
          </ChartContainer>
        </CardBody>
      </ChartCard>
      <ChartCard
        gradientPos={gradientPositions[3].pos1}
        gradientPos2={gradientPositions[3].pos2}
      >
        <CardBody>
          <ChartTitle>Top Rated Companies</ChartTitle>
          <ChartContainer>
            <canvas id="topRatedCompaniesChart"></canvas>
          </ChartContainer>
        </CardBody>
      </ChartCard>{" "}
      <ChartCard
        gradientPos={gradientPositions[0].pos1}
        gradientPos2={gradientPositions[0].pos2}
      >
        <CardBody>
          <ChartTitle>Top Companies by Internship Count</ChartTitle>
          <ChartContainer>
            <canvas id="topCompaniesByCountChart"></canvas>
          </ChartContainer>
        </CardBody>
      </ChartCard>
      {/* New Report Generator Card */}
      <ReportGenerator
        reportStats={reportStats}
        reviewTimeStats={reviewTimeStats}
        courseStats={courseStats}
        companyRatingStats={companyRatingStats}
        internshipCountStats={internshipCountStats}
      />
    </ChartsContainer>
  );
};

export default DetailedStatisticsCharts;
